import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import snipeHubService from '../../utils/services/snipeHubService';
import AFL_PLAYERS from '../../assets/static_data/afl_players.json';
import { BookmakerLogo, bookmakerRank } from '../HotProps/bookmakerAssets';

// lowercase player name → static player record (image_src etc.), for avatars.
const NAME_INDEX = new Map(AFL_PLAYERS.map((p) => [p.player_name?.trim().toLowerCase(), p]));

const POLL_MS = 60000;          // refresh cadence
const PAGE_SIZE = 120;          // rows rendered per "show more" page
const MIN_PCT_THRESHOLD = 2;    // server filters movements below ±2%, so this is the floor

// Per-match filter state kept alive for the SPA session, so leaving the match
// page and coming back doesn't reset the feed's filters. Keyed by matchId.
const feedStateCache = new Map();

// Stable identity for one movement event — used to diff polls so only
// genuinely new events animate in / bump the unread badge.
function movementKey(m) {
    return `${m.player}|${m.market}|${m.bookmaker}|${m.timestamp}|${m.previous_price}|${m.new_price}`;
}

function formatPrice(p) {
    return p != null ? Number(p).toFixed(2) : '—';
}

function formatPct(p) {
    if (p == null) return null;
    return `${p >= 0 ? '+' : ''}${Number(p).toFixed(1)}%`;
}

function timeAgo(ts, now) {
    const t = new Date(ts).getTime();
    if (Number.isNaN(t)) return '';
    const s = Math.max(0, (now - t) / 1000);
    if (s < 45) return 'just now';
    if (s < 90) return '1m ago';
    const m = s / 60;
    if (m < 60) return `${Math.round(m)}m ago`;
    const h = m / 60;
    if (h < 24) return `${Math.round(h)}h ago`;
    return `${Math.round(h / 24)}d ago`;
}

function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function FeedPlayerAvatar({ playerName, size = 34 }) {
    const [errored, setErrored] = useState(false);
    useEffect(() => { setErrored(false); }, [playerName]);

    const record = NAME_INDEX.get(playerName?.trim().toLowerCase());
    const dim = { width: size, height: size };

    if (errored || !record?.image_src) {
        return (
            <div
                style={dim}
                className="rounded-full bg-gradient-to-br from-slate-700 to-slate-800 ring-1 ring-slate-700/60 flex items-center justify-center flex-shrink-0"
            >
                <span className="font-mono font-bold text-emerald-300/90" style={{ fontSize: size * 0.32 }}>
                    {getInitials(playerName)}
                </span>
            </div>
        );
    }

    return (
        <div style={dim} className="rounded-full bg-slate-800 ring-1 ring-slate-700/60 overflow-hidden flex-shrink-0">
            <img
                src={record.image_src}
                alt={playerName}
                width={size}
                height={size}
                loading="lazy"
                onError={() => setErrored(true)}
                className="w-full h-full object-cover object-top"
            />
        </div>
    );
}

// Chip-styled trigger that opens a popover menu — same pattern as the Prop
// Tool / market overview filter chips. Closes on outside click.
function ChipDropdown({ summary, active, panelWidth = 'w-48', children }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={open}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-mono font-medium border transition-all ${
                    active
                        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                        : 'bg-slate-900/50 text-slate-400 border-slate-700/60 hover:text-slate-200 hover:border-slate-600'
                }`}
            >
                <span className="truncate max-w-[130px]">{summary}</span>
                <svg className={`w-3 h-3 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {open && (
                <div className={`absolute z-50 left-0 mt-1.5 ${panelWidth} max-h-64 overflow-y-auto styled-scrollbar rounded-xl border border-slate-700/70 bg-slate-900 shadow-2xl shadow-black/60`}>
                    {children({ close: () => setOpen(false) })}
                </div>
            )}
        </div>
    );
}

// A single row inside a ChipDropdown menu (tick marks the selection).
function ChipMenuItem({ label, checked, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-[12px] transition-colors hover:bg-slate-800/60"
        >
            <span className="w-3.5 flex-shrink-0">
                {checked && (
                    <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                    </svg>
                )}
            </span>
            <span className={`flex-1 flex items-center gap-2 truncate font-mono ${checked ? 'text-white' : 'text-slate-300'}`}>{label}</span>
        </button>
    );
}

// "Find markets" jump — same icon/tooltip as the CBA / Ruck / Kick-In tables.
// The tooltip is portaled so the drawer's scroll container can't clip it.
function FindMarketsButton({ player, onClick }) {
    const ref = useRef(null);
    const [tip, setTip] = useState(null);

    const show = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const maxWidth = 240;
        const left = Math.max(8, Math.min(r.left, window.innerWidth - maxWidth - 8));
        const below = r.bottom + 6;
        const flipUp = below + 36 > window.innerHeight;
        setTip({ left, top: flipUp ? r.top - 6 : below, flipUp, maxWidth });
    }, []);
    const hide = useCallback(() => setTip(null), []);

    const activate = (e) => { e.stopPropagation(); hide(); onClick(); };

    return (
        <span ref={ref} className="inline-flex flex-shrink-0">
            <button
                type="button"
                onClick={activate}
                onMouseEnter={show}
                onMouseLeave={hide}
                onFocus={show}
                onBlur={hide}
                aria-label={`Find markets for ${player}`}
                className="inline-flex items-center justify-center w-5 h-5 rounded text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/10 hover:border-emerald-500/50 hover:text-emerald-300 focus:outline-none cursor-pointer transition-all duration-150"
            >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 4v6a2 2 0 002 2h10m0 0l-4-4m4 4l-4 4" />
                </svg>
            </button>
            {tip && createPortal(
                <span
                    role="tooltip"
                    style={{
                        position: 'fixed',
                        left: tip.left,
                        top: tip.top,
                        maxWidth: tip.maxWidth,
                        transform: tip.flipUp ? 'translateY(-100%)' : 'none',
                        zIndex: 9999,
                    }}
                    className="pointer-events-none px-2.5 py-1.5 rounded-lg
                        bg-slate-900 border border-slate-700/60 shadow-xl shadow-black/40
                        whitespace-nowrap normal-case tracking-normal animate-fade-in"
                >
                    <span className="block text-[11px] font-mono font-normal text-slate-200">
                        Find markets <span className="text-slate-500">-</span> {player}
                    </span>
                </span>,
                document.body
            )}
        </span>
    );
}

// One movement event in the feed. `fresh` events (arrived on the latest poll)
// slide in and flash their timeline dot.
const FeedItem = React.memo(function FeedItem({ movement, fresh, now, onFindMarkets }) {
    const up = movement.direction === 'up';
    const dirText = up ? 'text-emerald-400' : 'text-red-400';
    const dirChip = up ? 'text-emerald-400 bg-emerald-500/10' : 'text-red-400 bg-red-500/10';
    const totalPct = formatPct(movement.total_percentage_change);

    return (
        <li className={`relative pl-10 pr-4 py-3 ${fresh ? 'feed-item-enter' : ''}`}>
            {/* Timeline rail dot */}
            <span
                className={`absolute left-[15px] top-[22px] w-[7px] h-[7px] rounded-full ring-4 ring-slate-950 ${
                    up ? 'bg-emerald-400' : 'bg-red-400'
                } ${fresh ? 'feed-dot-flash' : ''}`}
            />

            <div className="flex items-start gap-2.5">
                <FeedPlayerAvatar playerName={movement.player} />
                <div className="flex-1 min-w-0">
                    {/* Player + time */}
                    <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-1.5 min-w-0">
                            <span className="text-[12px] font-semibold text-white truncate">{movement.player}</span>
                            {onFindMarkets && (
                                <FindMarketsButton
                                    player={movement.player}
                                    onClick={() => onFindMarkets(movement)}
                                />
                            )}
                        </span>
                        <span className="text-[9px] font-mono text-slate-500 whitespace-nowrap flex-shrink-0">
                            {timeAgo(movement.timestamp, now)}
                        </span>
                    </div>

                    {/* Market line + bookmaker */}
                    <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                        <span className="text-[10px] font-mono text-emerald-400/80 truncate">{movement.market}</span>
                        <span className="text-slate-700 text-[10px]">·</span>
                        {movement.bookmaker_url ? (
                            <a
                                href={movement.bookmaker_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={`Open ${movement.bookmaker_display} market`}
                                className="flex items-center gap-1 flex-shrink-0 group/book"
                            >
                                <BookmakerLogo bookmaker={movement.bookmaker} size={12} />
                                <span className="text-[9px] font-mono text-slate-400 group-hover/book:text-slate-200 transition-colors">
                                    {movement.bookmaker_display}
                                </span>
                                <svg className="w-2.5 h-2.5 text-slate-600 group-hover/book:text-slate-400 transition-colors" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                </svg>
                            </a>
                        ) : (
                            <span className="flex items-center gap-1 flex-shrink-0">
                                <BookmakerLogo bookmaker={movement.bookmaker} size={12} />
                                <span className="text-[9px] font-mono text-slate-400">{movement.bookmaker_display}</span>
                            </span>
                        )}
                        {movement.is_derived && (
                            <span
                                title="Derived price — modelled from this bookmaker's other odds, not directly bettable"
                                className="flex-shrink-0 px-1 py-px rounded text-[8px] font-mono font-semibold uppercase tracking-wider text-amber-400 bg-amber-500/10 border border-amber-500/20"
                            >
                                derived
                            </span>
                        )}
                    </div>

                    {/* Price movement */}
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1.5">
                        <span className="flex items-center gap-1.5 text-[13px] font-mono tabular-nums">
                            <span className="text-slate-500 line-through decoration-slate-600">{formatPrice(movement.previous_price)}</span>
                            <svg className={`w-3 h-3 ${dirText}`} fill="none" stroke="currentColor" strokeWidth="2.25" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                            </svg>
                            <span className={`font-bold ${dirText}`}>{formatPrice(movement.new_price)}</span>
                        </span>
                        <span className={`flex items-center gap-0.5 px-1.5 py-px rounded-md text-[10px] font-mono font-semibold tabular-nums ${dirChip}`}>
                            <svg className={`w-2.5 h-2.5 ${up ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                            </svg>
                            {formatPct(movement.price_change_percentage)}
                        </span>
                    </div>

                    {/* Line journey at this book: open → current */}
                    <div className="mt-1 text-[9px] font-mono text-slate-600">
                        opened <span className="text-slate-400">{formatPrice(movement.open_price)}</span>
                        <span className="text-slate-700"> → </span>
                        now <span className="text-slate-400">{formatPrice(movement.current_price)}</span>
                        {totalPct && (
                            <span className={movement.total_percentage_change >= 0 ? 'text-emerald-500/80' : 'text-red-400/80'}>
                                {' '}({totalPct} total)
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </li>
    );
});

/**
 * Live price-movement feed for one match: a floating "Live Price Movement
 * Feed" pill that expands a right-hand drawer streaming every price change
 * across all players, lines and bookmakers, newest first.
 *
 * Loads the full feed once, then polls every minute with a `since` cursor
 * (the newest event's timestamp) so each poll only transfers new events —
 * the full feed can be several MB, incremental polls are a few KB. New
 * events are prepended and animate in at the top. While the drawer is
 * collapsed, new events accumulate on the pill as an unread badge.
 */
function MatchPriceMovementFeed({ matchId, onFindMarkets }) {
    // Restore filters from a previous visit to this match, if any.
    const cached = feedStateCache.get(matchId);

    const [open, setOpen] = useState(false);
    const [movements, setMovements] = useState(null);   // null = not loaded yet
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [unread, setUnread] = useState(0);
    const [freshKeys, setFreshKeys] = useState(() => new Set());
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [now, setNow] = useState(() => Date.now());
    const [lastUpdated, setLastUpdated] = useState(null);

    // Feed filters.
    const [playerQuery, setPlayerQuery] = useState(cached?.playerQuery ?? '');
    const [bookFilter, setBookFilter] = useState(cached?.bookFilter ?? 'all');
    const [sortBy, setSortBy] = useState(cached?.sortBy ?? 'latest');          // 'latest' | 'biggest'
    const [directionFilter, setDirectionFilter] = useState(cached?.directionFilter ?? 'all'); // 'all' | 'up' | 'down'
    // Min |movement %|. The API never returns moves under ±2%, so 2 is both
    // the default and the lowest selectable threshold.
    const [pctThreshold, setPctThreshold] = useState(() => Math.max(MIN_PCT_THRESHOLD, cached?.pctThreshold ?? MIN_PCT_THRESHOLD));

    // Keep the filters alive for the SPA session.
    useEffect(() => {
        if (!matchId) return;
        feedStateCache.set(matchId, { playerQuery, bookFilter, sortBy, directionFilter, pctThreshold });
    }, [matchId, playerQuery, bookFilter, sortBy, directionFilter, pctThreshold]);

    // Refs so the poll callback stays stable without re-arming the interval.
    // `sinceRef` holds the newest seen event's timestamp — the `since` cursor
    // for incremental polls. Null means the next fetch pulls the full feed.
    const sinceRef = useRef(null);
    const openRef = useRef(open);
    openRef.current = open;
    const stoppedRef = useRef(false);   // stop polling on hard 404s

    const fetchFeed = useCallback(async ({ initial = false } = {}) => {
        if (!matchId || stoppedRef.current) return;
        if (initial) setIsLoading(true);

        const cursor = initial ? null : sinceRef.current;
        const result = await snipeHubService.getAflPriceMovementFeed(matchId, cursor);

        if (result.success) {
            const list = result.data?.movements || [];

            if (cursor) {
                // Incremental poll — the `since` filter is strict, so every
                // returned event is new. Prepend them (feed is newest-first)
                // and advance the cursor only when something arrived.
                if (list.length > 0) {
                    sinceRef.current = list[0]?.timestamp || cursor;
                    setFreshKeys(new Set(list.map(movementKey)));
                    if (!openRef.current) setUnread((u) => u + list.length);
                    setMovements((prev) => [...list, ...(prev || [])]);
                } else {
                    setFreshKeys(new Set());
                }
            } else {
                // Full feed (first load, retry, or no events seen yet).
                sinceRef.current = list[0]?.timestamp || null;
                setFreshKeys(new Set());
                setMovements(list);
            }

            setError(null);
            setLastUpdated(Date.now());
        } else {
            if (result.errorCode === 'GAME_NOT_FOUND' || result.errorCode === 'VALIDATION_ERROR') {
                stoppedRef.current = true;
            }
            // Keep showing the last good feed on transient errors; only surface
            // the error state when we have nothing to show.
            setError(result);
        }
        setNow(Date.now());
        setIsLoading(false);
    }, [matchId]);

    // Initial full load + incremental poll every minute.
    useEffect(() => {
        stoppedRef.current = false;
        sinceRef.current = null;
        setMovements(null);
        setUnread(0);
        setFreshKeys(new Set());
        setVisibleCount(PAGE_SIZE);

        fetchFeed({ initial: true });
        const id = setInterval(() => fetchFeed(), POLL_MS);
        return () => clearInterval(id);
    }, [fetchFeed]);

    // Keep "x m ago" labels fresh between polls.
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(id);
    }, []);

    // Opening the drawer clears the unread badge.
    const toggleOpen = useCallback(() => {
        setOpen((v) => {
            if (!v) setUnread(0);
            return !v;
        });
    }, []);

    // Escape closes the drawer.
    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open]);

    // Jump to the game market overview filtered to this movement's player and
    // market category. The drawer stays open so the user can keep browsing.
    const handleFindMarkets = useCallback((movement) => {
        if (!onFindMarkets) return;
        const categoryKey = movement.market_category
            ? movement.market_category.trim().toLowerCase().replace(/\s+/g, '_')
            : null;
        onFindMarkets(movement.player, categoryKey);
    }, [onFindMarkets]);

    // Bookmakers present in the feed, in the canonical display order.
    const feedBooks = useMemo(() => {
        const map = new Map();
        (movements || []).forEach((m) => {
            if (!map.has(m.bookmaker)) map.set(m.bookmaker, m.bookmaker_display || m.bookmaker);
        });
        return [...map.entries()]
            .sort((a, b) => bookmakerRank(a[0]) - bookmakerRank(b[0]) || a[1].localeCompare(b[1]))
            .map(([key, display]) => ({ key, display }));
    }, [movements]);

    // Player search + bookmaker + direction filters, then the active sort.
    // "Latest" keeps the feed's native newest-first order.
    const filtered = useMemo(() => {
        let list = movements || [];
        const q = playerQuery.trim().toLowerCase();
        if (q) list = list.filter((m) => (m.player || '').toLowerCase().includes(q));
        if (bookFilter !== 'all') list = list.filter((m) => m.bookmaker === bookFilter);
        if (directionFilter !== 'all') list = list.filter((m) => m.direction === directionFilter);
        if (pctThreshold > 0) {
            list = list.filter((m) => Math.abs(m.price_change_percentage ?? 0) >= pctThreshold);
        }
        if (sortBy === 'biggest') {
            list = [...list].sort((a, b) =>
                Math.abs(b.price_change_percentage ?? 0) - Math.abs(a.price_change_percentage ?? 0)
            );
        }
        return list;
    }, [movements, playerQuery, bookFilter, directionFilter, pctThreshold, sortBy]);

    // Changing any filter resets the pager.
    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [playerQuery, bookFilter, directionFilter, pctThreshold, sortBy]);

    const visible = useMemo(
        () => filtered.slice(0, visibleCount),
        [filtered, visibleCount]
    );

    const hasFeed = (movements?.length || 0) > 0;
    const filtersActive = playerQuery.trim() !== '' || bookFilter !== 'all' || directionFilter !== 'all' || pctThreshold > MIN_PCT_THRESHOLD;
    const activeBookDisplay = bookFilter === 'all'
        ? 'All Bookmakers'
        : (feedBooks.find((b) => b.key === bookFilter)?.display || bookFilter);
    const badge = unread > 99 ? '99+' : unread;

    return createPortal(
        <>
            {/* Floating toggle pill */}
            <button
                type="button"
                onClick={toggleOpen}
                aria-expanded={open}
                className={`fixed bottom-5 right-5 z-[70] flex items-center gap-2 pl-3 pr-3.5 py-2.5 rounded-full border shadow-2xl shadow-black/50 transition-all duration-200 ${
                    open
                        ? 'bg-slate-900 border-slate-700/70 text-slate-300 hover:text-white hover:border-slate-600'
                        : 'bg-gradient-to-r from-slate-900 to-slate-900/95 border-emerald-500/40 text-emerald-300 hover:border-emerald-400/60 hover:shadow-emerald-500/10'
                }`}
            >
                <span className="relative flex h-2 w-2 flex-shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-50" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                <span className="text-[11px] font-mono font-semibold whitespace-nowrap">
                    {open ? 'Close Feed' : 'Live Price Movement Feed'}
                </span>
                {!open && unread > 0 && (
                    <span className="feed-badge-pop flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-500 text-slate-950 text-[10px] font-mono font-bold">
                        {badge}
                    </span>
                )}
            </button>

            {/* Right-hand drawer */}
            <aside
                aria-hidden={!open}
                className={`fixed top-0 right-0 bottom-0 z-[69] w-full sm:w-[420px] flex flex-col
                    bg-slate-950/[0.97] border-l border-slate-800/80 shadow-[-24px_0_60px_-20px_rgba(0,0,0,0.8)]
                    transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]
                    ${open ? 'translate-x-0' : 'translate-x-full pointer-events-none'}`}
            >
                {/* Emerald hairline + glow, matching the app's modal chrome */}
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/60 to-transparent" />
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-20 bg-emerald-500/10 blur-3xl pointer-events-none" />

                {/* Header */}
                <div className="relative flex items-center gap-2.5 px-4 py-3.5 border-b border-slate-800/80 flex-shrink-0">
                    <span className="relative flex h-2 w-2 flex-shrink-0">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-50" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                    </span>
                    <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-mono font-semibold text-white uppercase tracking-[0.12em]">
                            Live Price Movement Feed
                        </div>
                        <div className="text-[9px] font-mono text-slate-500 mt-0.5 truncate">
                            {hasFeed && <span className="text-slate-400">{movements.length.toLocaleString()} movements</span>}
                            {hasFeed && <span className="text-slate-700"> · </span>}
                            updates every 60s
                            {lastUpdated && (
                                <>
                                    <span className="text-slate-700"> · </span>
                                    synced {timeAgo(lastUpdated, now)}
                                </>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => fetchFeed()}
                        disabled={isLoading}
                        title="Refresh now"
                        className="p-1.5 rounded-lg text-slate-400 border border-slate-700/70 hover:bg-slate-800/60 hover:text-slate-200 hover:border-slate-600 transition-all disabled:opacity-50"
                    >
                        <svg className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        onClick={() => setOpen(false)}
                        aria-label="Collapse feed"
                        className="p-1.5 rounded-lg text-slate-400 border border-slate-700/70 hover:bg-slate-800/60 hover:text-slate-200 hover:border-slate-600 transition-all"
                    >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>

                {/* Filters */}
                {hasFeed && (
                    <div className="relative px-4 py-2.5 border-b border-slate-800/80 flex-shrink-0 space-y-2">
                        {/* Player search */}
                        <div className="relative">
                            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-600 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                type="text"
                                placeholder="Search player..."
                                value={playerQuery}
                                onChange={(e) => setPlayerQuery(e.target.value)}
                                className="w-full pl-8 pr-7 py-1.5 bg-slate-900/50 border border-slate-700/70 rounded-lg text-[11px] font-mono text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500/60 transition-colors"
                            />
                            {playerQuery && (
                                <button
                                    type="button"
                                    onClick={() => setPlayerQuery('')}
                                    aria-label="Clear player search"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200 transition-colors"
                                >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>

                        <div className="flex flex-wrap items-center gap-1.5">
                            {/* Bookmaker filter */}
                            <ChipDropdown
                                active={bookFilter !== 'all'}
                                summary={(
                                    <span className="flex items-center gap-1.5">
                                        {bookFilter !== 'all' && <BookmakerLogo bookmaker={bookFilter} size={13} />}
                                        {activeBookDisplay}
                                    </span>
                                )}
                            >
                                {({ close }) => (
                                    <>
                                        <ChipMenuItem
                                            label="All Bookmakers"
                                            checked={bookFilter === 'all'}
                                            onClick={() => { setBookFilter('all'); close(); }}
                                        />
                                        {feedBooks.map((b) => (
                                            <ChipMenuItem
                                                key={b.key}
                                                checked={bookFilter === b.key}
                                                onClick={() => { setBookFilter(b.key); close(); }}
                                                label={(
                                                    <>
                                                        <BookmakerLogo bookmaker={b.key} size={14} />
                                                        {b.display}
                                                    </>
                                                )}
                                            />
                                        ))}
                                    </>
                                )}
                            </ChipDropdown>

                            {/* Sort */}
                            <ChipDropdown
                                active={sortBy !== 'latest'}
                                panelWidth="w-44"
                                summary={sortBy === 'latest' ? 'Latest' : 'Biggest Movement'}
                            >
                                {({ close }) => (
                                    <>
                                        <ChipMenuItem
                                            label="Latest"
                                            checked={sortBy === 'latest'}
                                            onClick={() => { setSortBy('latest'); close(); }}
                                        />
                                        <ChipMenuItem
                                            label="Biggest Movement"
                                            checked={sortBy === 'biggest'}
                                            onClick={() => { setSortBy('biggest'); close(); }}
                                        />
                                    </>
                                )}
                            </ChipDropdown>

                            {/* Movement % threshold — 2% is the feed's floor */}
                            <ChipDropdown
                                active={pctThreshold > MIN_PCT_THRESHOLD}
                                panelWidth="w-36"
                                summary={`${pctThreshold}+% moves`}
                            >
                                {({ close }) => [2, 5, 10, 15, 25].map((t) => (
                                    <ChipMenuItem
                                        key={t}
                                        label={`${t}+%`}
                                        checked={pctThreshold === t}
                                        onClick={() => { setPctThreshold(t); close(); }}
                                    />
                                ))}
                            </ChipDropdown>

                            {/* Direction toggles — click again to clear */}
                            <button
                                type="button"
                                onClick={() => setDirectionFilter((d) => (d === 'up' ? 'all' : 'up'))}
                                aria-pressed={directionFilter === 'up'}
                                className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-[10px] font-mono font-medium border transition-all ${
                                    directionFilter === 'up'
                                        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                                        : 'bg-slate-900/50 text-slate-400 border-slate-700/60 hover:text-slate-200 hover:border-slate-600'
                                }`}
                            >
                                <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                                </svg>
                                Drifts
                            </button>
                            <button
                                type="button"
                                onClick={() => setDirectionFilter((d) => (d === 'down' ? 'all' : 'down'))}
                                aria-pressed={directionFilter === 'down'}
                                className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-[10px] font-mono font-medium border transition-all ${
                                    directionFilter === 'down'
                                        ? 'bg-red-500/15 text-red-300 border-red-500/40'
                                        : 'bg-slate-900/50 text-slate-400 border-slate-700/60 hover:text-slate-200 hover:border-slate-600'
                                }`}
                            >
                                <svg className="w-2.5 h-2.5 rotate-180" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                                </svg>
                                Shortens
                            </button>

                            {filtersActive && (
                                <span className="ml-auto text-[9px] font-mono text-slate-500">
                                    {filtered.length.toLocaleString()} match{filtered.length === 1 ? '' : 'es'}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* Body */}
                <div className="relative flex-1 overflow-y-auto styled-scrollbar">
                    {isLoading && !movements ? (
                        <div className="flex items-center gap-2.5 py-16 justify-center">
                            <div className="w-4 h-4 border-2 border-slate-700 border-t-emerald-400 rounded-full animate-spin" />
                            <span className="text-xs font-mono text-slate-500">Loading price movements…</span>
                        </div>
                    ) : error && !hasFeed ? (
                        <div className="py-16 px-6 flex flex-col items-center text-center gap-3">
                            <span className="text-xs font-mono text-slate-500 leading-relaxed">
                                {error.message || 'Unable to load the price movement feed.'}
                            </span>
                            {!stoppedRef.current && (
                                <button
                                    type="button"
                                    onClick={() => fetchFeed({ initial: true })}
                                    className="px-3 py-1.5 text-[11px] font-mono font-medium text-slate-300 border border-slate-700 rounded-lg hover:bg-slate-800 hover:border-slate-600 transition-all"
                                >
                                    Retry
                                </button>
                            )}
                        </div>
                    ) : !hasFeed ? (
                        <div className="py-16 px-6 text-center">
                            <span className="text-xs font-mono text-slate-500 leading-relaxed">
                                No price movements yet. Movements will stream in here as bookmakers adjust their lines.
                            </span>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="py-16 px-6 flex flex-col items-center text-center gap-3">
                            <span className="text-xs font-mono text-slate-500 leading-relaxed">
                                No movements match your filters.
                            </span>
                            <button
                                type="button"
                                onClick={() => { setPlayerQuery(''); setBookFilter('all'); setDirectionFilter('all'); setPctThreshold(MIN_PCT_THRESHOLD); }}
                                className="px-3 py-1.5 text-[11px] font-mono font-medium text-slate-300 border border-slate-700 rounded-lg hover:bg-slate-800 hover:border-slate-600 transition-all"
                            >
                                Clear filters
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* Timeline rail */}
                            <div className="absolute left-[18px] top-0 bottom-0 w-px bg-slate-800/70 pointer-events-none" />
                            <ul className="relative divide-y divide-slate-800/50">
                                {visible.map((m) => {
                                    const key = movementKey(m);
                                    return (
                                        <FeedItem
                                            key={key}
                                            movement={m}
                                            fresh={freshKeys.has(key)}
                                            now={now}
                                            onFindMarkets={onFindMarkets ? handleFindMarkets : undefined}
                                        />
                                    );
                                })}
                            </ul>
                            {filtered.length > visibleCount && (
                                <div className="p-3 text-center">
                                    <button
                                        type="button"
                                        onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                                        className="px-3.5 py-1.5 text-[11px] font-mono font-medium text-slate-300 border border-slate-700 rounded-lg hover:bg-slate-800 hover:border-slate-600 transition-all"
                                    >
                                        Show more ({(filtered.length - visibleCount).toLocaleString()} {sortBy === 'latest' ? 'older' : 'more'})
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer legend */}
                <div className="relative flex items-center gap-3 px-4 py-2.5 border-t border-slate-800/80 flex-shrink-0">
                    <span className="flex items-center gap-1.5 text-[9px] font-mono text-slate-500">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> price drifted out
                    </span>
                    <span className="flex items-center gap-1.5 text-[9px] font-mono text-slate-500">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-400" /> price shortened
                    </span>
                </div>
            </aside>

            <style>{`
                @keyframes feedItemEnter {
                    0% { opacity: 0; transform: translateY(-10px); background-color: rgba(16, 185, 129, 0.08); }
                    60% { background-color: rgba(16, 185, 129, 0.08); }
                    100% { opacity: 1; transform: translateY(0); background-color: transparent; }
                }
                .feed-item-enter {
                    animation: feedItemEnter 0.9s cubic-bezier(0.22, 1, 0.36, 1) both;
                }
                @keyframes feedDotFlash {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
                    40% { box-shadow: 0 0 0 5px rgba(16, 185, 129, 0.25); }
                }
                .feed-dot-flash {
                    animation: feedDotFlash 1.2s ease-out 2;
                }
                @keyframes feedBadgePop {
                    0% { transform: scale(0.4); opacity: 0; }
                    70% { transform: scale(1.15); }
                    100% { transform: scale(1); opacity: 1; }
                }
                .feed-badge-pop {
                    animation: feedBadgePop 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) both;
                }
                @media (prefers-reduced-motion: reduce) {
                    .feed-item-enter, .feed-dot-flash, .feed-badge-pop { animation: none; }
                }
            `}</style>
        </>,
        document.body
    );
}

export default MatchPriceMovementFeed;
